import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Stack,
  Box,
  Grow,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  Person,
  Star,
  Bookmark,
  BookmarkBorder,
} from '@mui/icons-material';
import BookMyOpAPI from '../services/BookMyOpapi';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import { useFavorites } from '../contexts/FavoritesContext';

const DoctorCard = ({ doctor, index, onClick, showAnimation = true }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useBookMyOpAuth();
  const { isDoctorFavorite } = useFavorites();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Get favorite status from context
  const isFavorite = isDoctorFavorite(doctor.id);

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await BookMyOpAPI.toggleFavorite({ doctor_id: doctor.id });
      setSnackbar({
        open: true,
        message: response.is_favorite ? 'Added to favorites!' : 'Removed from favorites',
        severity: 'success'
      });
      
      // Dispatch custom event to notify context to refresh
      window.dispatchEvent(new CustomEvent('favoritesUpdated', { 
        detail: { doctorId: doctor.id, isFavorite: response.is_favorite } 
      }));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update favorites',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleClick = () => {
    if (onClick) {
      onClick(doctor.id);
    } else {
      navigate(`/doctors/${doctor.id}`);
    }
  };

  const handleBookClick = (e) => {
    e.stopPropagation();
    handleClick();
  };

  const rating = doctor.star_rating;

  const cardContent = (
    <Card
      sx={{
        height: { xs: 280, md: 280 },
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        borderRadius: 2.5,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        overflow: 'visible',
        position: 'relative',
        background: `linear-gradient(135deg, ${theme.palette.primary.light}90, ${theme.palette.background.paper}40)`,
       backdropFilter: 'blur(10px)',
        border: `1px solid ${theme.palette.background.paper}10`,
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 20px 40px ${theme.palette.primary.main}26`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}40, ${theme.palette.primary.light}20)`,
          zIndex: 10,
          '& .doctor-avatar': {
            transform: 'scale(1.1)',
          },
        },
      }}
      onClick={handleClick}
    >
      {/* Bookmark Icon - Top Right Corner of Card */}
      <Button
        onClick={handleToggleFavorite}
        disabled={loading}
        startIcon={
          isFavorite ? (
            <Bookmark sx={{ fontSize: { xs: 18, md: 20 }, color: '#ff6b6b' }} />
          ) : (
            <BookmarkBorder sx={{ fontSize: { xs: 18, md: 20 }, color: '#666' }} />
          )
        }
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          minWidth: 'auto',
          height: { xs: 32, md: 36 },
          bgcolor: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          border: '2px solid',
          borderColor: isFavorite ? theme.palette.primary.light : 'grey.300',
          borderRadius: 2,
          zIndex: 3,
          px: 1,
          py: 0.5,
          fontSize: '0.75rem',
          fontWeight: 600,
          color: isFavorite ? '#ff6b6b' : '#666',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '&:hover': {
            bgcolor: isFavorite ? '#ffe5e5' : 'grey.50',
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {isFavorite ? 'Saved' : 'Save'}
      </Button>

      <CardContent sx={{ p: { xs: 2, md: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
        {/* First Row - Avatar and Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: 1 }}>
          {/* Doctor Avatar/Image */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            {doctor.profile_photo ? (
              <Avatar
                src={doctor.profile_photo}
                alt={doctor.name}
                className="doctor-avatar"
                sx={{
                  width: { xs: 50, md: 60 },
                  height: { xs: 50, md: 60 },
                  border: '2px solid #fff',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                  transition: 'transform 0.3s ease',
                }}
              />
            ) : (
              <Avatar
                className="doctor-avatar"
                sx={{
                  width: { xs: 50, md: 60 },
                  height: { xs: 50, md: 60 },
                  bgcolor: 'primary.main',
                  border: '2px solid #fff',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <Person sx={{ fontSize: { xs: 22, md: 28 } }} />
              </Avatar>
            )}
          </Box>

          {/* Doctor Name & Specialty */}
          <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 4, md: 5 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800, 
                fontSize: { xs: '1.1rem', md: '1.2rem' },
                lineHeight: 1.2,
                mb: 0.3,
                color: '#1a1a1a',
              }}
            >
              Dr. {doctor.name}
            </Typography>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '0.85rem', md: '0.95rem' },
                lineHeight: 1.2,
              }}
            >
              {doctor.specialty_name}
            </Typography>
          </Box>
        </Box>

        {/* Second Row - All Other Info */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

          {/* Hospital Name */}
          <Box sx={{ mb: { xs: 0.5, md: 0.8 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LocationOn sx={{ fontSize: { xs: 14, md: 16 }, color: '#666' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '1.1rem', md: '1.1rem' },
                  fontWeight: 800,
                  color: '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {doctor.service_provider_name}
              </Typography>
              {doctor.distance_km && (
            <Box sx={{ mb: { xs: 1, md: 1.2 } }}>
              <Chip
                label={`${parseFloat(doctor.distance_km).toFixed(1)} km`}
                size="small"
                sx={{
                  height: { xs: 20, md: 22 },
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                  bgcolor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              />
            </Box>
          )}
            </Stack>
            
          </Box>

          {/* Distance */}
       

          {/* Rating & Experience */}
          <Box sx={{ mb: { xs: 1, md: 1.2 } }}>
            {/* Rating and Experience in one row */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
              {/* Rating */}
              {rating !== null && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Star sx={{ fontSize: '1rem', color: '#ffa726' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', md: '0.9rem' }, fontWeight: 700 }}>
                    {rating}
                  </Typography>
                </Stack>
              )}
              
              {/* Experience */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <AccessTime sx={{ fontSize: { xs: 12, md: 14 }, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', md: '0.9rem' }, fontWeight: 700 }}>
                  {doctor.experience_years}y exp
                </Typography>
              </Stack>
            </Stack>
            
            {/* Qualifications */}
            {doctor.qualifications && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.8rem' }, fontWeight: 700, display: 'block', mt: 0.3 }}>
                {doctor.qualifications.name || doctor.qualifications.abbreviation}
              </Typography>
            )}
          </Box>

          {/* Footer - Price & Book Button */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: '10px', pb: 0 }}>
            <Box>
              <Typography 
                variant="h6" 
                color="primary" 
                sx={{ 
                  fontWeight: 800, 
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.2,
                  mb: 0.3,
                }}
              >
                ₹{Math.round(parseFloat(doctor.consultation_fee))}
              </Typography>
              {doctor.discount_percentage && parseFloat(doctor.discount_percentage) > 0 && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '0.8rem', md: '0.8rem' },
                    display: 'block',
                    lineHeight: 1.2,
                  }}
                >
                  {doctor.discount_percentage}% off
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              size="small"
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                px: { xs: 1.5, md: 2 },
                py: { xs: 0.5, md: 0.6 },
                minWidth: { xs: '80px', md: '90px' },
                height: { xs: '32px', md: '36px' },
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              }}
              onClick={handleBookClick}
            >
              View Details
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );

  const component = (
    <>
      {showAnimation ? (
        <Grow
          in={true}
          timeout={500 + (index % 4) * 200}
          style={{ transformOrigin: '0 0 0' }}
        >
          {cardContent}
        </Grow>
      ) : (
        cardContent
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  return component;
};

export default DoctorCard;
